import React, {
  useRef,
  useState,
  FunctionComponent,
  ChangeEvent,
  SyntheticEvent,
  useEffect,
} from 'react'
import Octicon, { Search, X } from '@primer/octicons-react'
import { SearchButton } from './styles'
import styles from '~/styles/searchInput.module.css'

interface Props {
  onSearch: (value: string) => void
  value?: string
  placeholder?: string,
  show?: boolean
  catchFocus?: boolean
}

const detectOrderId = value => {
  const orderIdSymbol = '&Id='
  const orderIdSymbolRus = '?Шв='

  if (value && value.indexOf(orderIdSymbol) > -1) {
    const regExp = new RegExp(`(?<=${orderIdSymbol}).*$`)
    return regExp.exec(value)[0] ?? ''
  } else if (value && value.indexOf(orderIdSymbolRus) > -1) {
    const regExpRus = new RegExp(`(?<=\\${orderIdSymbolRus}).*$`)
    return regExpRus.exec(value)[0] ?? ''
  }
}

let searchST

const SearchInput: FunctionComponent<Props> = ({
  value,
  onSearch,
  placeholder,
  show,
  catchFocus,
}: Props) => {
  if (show) {
    const [displayValue, setDefaultValue] = useState(value || '')
    const searchInput = useRef<HTMLInputElement>(null)
    const onKeyDown = (e: SyntheticEvent<HTMLFormElement>) => {
      if (e.keyCode === 13) {
        const value = searchInput?.current?.value || displayValue
        const orderId = detectOrderId(value  || displayValue)
        if (orderId) {
          try {
            const visibleID = orderId.match(/\d+/g).toString()
            searchInput.current.value = visibleID
            setDefaultValue(visibleID)
          } catch (err) {
            searchInput.current.value = ''
            setDefaultValue('')
          }
        }
        if (value) {
          onSearch(value)
        }
      }
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setDefaultValue(e.target.value)
      if (searchST) {
        clearTimeout(searchST)
      }
      searchST = setTimeout(() => {
        onSearch(e.target.value)
      }, 3000)
    }

    const globalKeyNumHandler = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.shiftKey || e.metaKey) {
        return
      }
      if (e.code.includes('Key') || e.code.includes('Digit')) {
        searchInput.current.focus()
      }
    }

    const onIcon = (event) => {
      if (displayValue && event.clientX) {
        setDefaultValue('')
        onSearch('')
      }      
    }

    useEffect(() => {
      catchFocus
        ? window.addEventListener('keydown', globalKeyNumHandler)
        : window.removeEventListener('keydown', globalKeyNumHandler)

      return () => window.removeEventListener('keydown', globalKeyNumHandler)
    }, [catchFocus])

    return (
      <div 
        className="relative w-64"
      >
        <input
          ref={searchInput}
          type="text"
          className={styles.input + " border border-gray-400 text-gray-800 bg-transparent rounded"}
          placeholder={placeholder}
          data-testid="search-field"
          onChange={handleChange}
          onKeyDown={onKeyDown}
          value={displayValue}
        />
        <SearchButton
          className="px-2 py-1 rounded text-sm text-gray-800 font-bold"
          data-testid="search-submit-button"
          onClick={onIcon}
        >
          <Octicon
            icon={displayValue ? X : Search}
          />
        </SearchButton>
      </div>
    )
  } else {
    return null
  }
}

export default SearchInput
